import * as React from "react";
import { ArrowRight } from "baseui/icon";
import { Block } from "../Block";
import { BlockProps } from "baseui/block";
import { Markdown } from "kanopi-components/Markdown";
import { StyleObject } from "styletron-react";
import { useStyletron } from "kanopi-components/theme/useStyletron";

import {
  DisplayXSmall,
  ParagraphLarge,
  ParagraphMedium,
  HeadingXSmall,
} from "../typography";

type TListProps = {
  items: Array<TListItemProps>;
  color?: string;
  size?: typeof List.SIZE;
};

type TListItemProps = {
  title?: string;
  description?: string;
  markdown?: string;
  withMargin?: boolean;
  color?: string;
  size?: typeof List.SIZE;
};

function List(props: TListProps) {
  const { items = [], color, size } = props;
  const [css] = useStyletron();
  return (
    <ol
      className={css({
        listStyle: "none",
        counterReset: "list-counter",
        paddingLeft: "45px",
      })}
    >
      {items.map((item, i) => (
        <ListItem {...item} key={i} withMargin={i !== 0} color={color} size={size} />
      ))}
    </ol>
  );
}

function ListItem(props: TListItemProps) {
  const { title, description, markdown, withMargin, color, size } = props;
  const [css, theme] = useStyletron();
  let Title, Description;
  if (size === "large") {
    Title = DisplayXSmall;
    Description = ParagraphLarge;
  } else {
    Title = HeadingXSmall;
    Description = ParagraphMedium;
  }
  return (
    <li
      className={css({
        position: "relative",
        marginTop: withMargin ? "50px" : 0,
        counterIncrement: "list-counter",
        "::before": getNumberStyle({ theme, color, size }),
      })}
    >
      {title && (
        <Title $style={{ marginBottom: "5px" }} color={color}>
          {title}
        </Title>
      )}
      {description && (
        <Description $style={{ marginTop: "5px" }} color={color}>
          {description}
        </Description>
      )}
      {markdown && <Markdown>{markdown}</Markdown>}
    </li>
  );
}

function getNumberStyle({ theme, color, size }): StyleObject {
  color = theme.colors[color] || theme.colors.contentPrimary;
  return {
    position: "absolute",
    left: "-40px",
    top: size === "large" ? "8px" : "0px",
    width: "25px",
    height: "25px",
    content: "counter(list-counter)",
    borderRadius: "50%",
    border: `1px solid ${color}`,
    color,
    lineHeight: "27px",
    textAlign: "center",
    fontFamily: theme.typography.font100.fontFamily,
  };
}

const UL: React.FC<BlockProps> = (props) => (
  <Block
    as="ul"
    paddingLeft={0}
    marginTop="scale600"
    marginBottom="scale600"
    {...props}
  />
);
const OL: React.FC<BlockProps> = (props) => (
  <Block
    as="ol"
    paddingLeft={0}
    marginTop="scale600"
    marginBottom="scale600"
    {...props}
  />
);
const LI: React.FC<BlockProps & { artwork?: React.ReactNode }> = ({
  children,
  artwork,
  ...props
}) => (
  <Block
    as="li"
    marginBottom="scale200"
    font="ParagraphMedium"
    display="block"
    color="mono700"
    {...props}
  >
    {artwork ? (
      <Block display="flex" alignItems="flex-start">
        <Block>{artwork}</Block>
        <Block marginLeft="scale400">{children}</Block>
      </Block>
    ) : (
      <>{children}</>
    )}
  </Block>
);

const ArrowedLI: React.FC<BlockProps> = (props) => {
  // `ordered` and `spread` cause a browser console error, so remove them.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ordered, spread, ...restProps } = props;
  return <LI artwork={<ArrowRight size={24} color="mono500" />} {...restProps} />;
};

List.SIZE = {
  large: "large",
};

export { List, UL, OL, LI, ArrowedLI };
